import React, { useState, useEffect } from "react";
import {
  Page,
  TextField,
  Checkbox,
  Text,
  FormLayout,
  Card,
  Button,
  Box,
  Select,
  Banner,
} from "@shopify/polaris";
import axios from "axios";
import { ContextualSaveBar } from "@shopify/polaris";
import { useNavigate } from "react-router-dom";

function Settings({ shopOrigin, apiKey }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    shopUrl: "",
    noContent: false,
    competitorUrl: "",
    keywords: "",
    ageRanges: [],
    gender: "All Genders",
    language: "en", // default language
  });

  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [initialFormData, setInitialFormData] = useState(formData);
  const [isLoading, setIsLoading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // language options
  const languageOptions = [
    { label: "English (United States)", value: "en" },
    { label: "English (United Kingdom)", value: "en-GB" },
    { label: "Arabic", value: "ar" },
    { label: "Bulgarian", value: "bg" },
    { label: "Chinese", value: "zh" },
    { label: "Chinese (Simplified)", value: "zh-CN" },
    { label: "Chinese (Traditional)", value: "zh-TW" },
    { label: "Dutch", value: "nl" },
    { label: "French", value: "fr" },
    { label: "German", value: "de" },
    { label: "Indonesian", value: "id" },
    { label: "Italian", value: "it" },
    { label: "Japanese", value: "ja" },
    { label: "Korean", value: "ko" },
    { label: "Polish", value: "pl" },
    { label: "Portuguese", value: "pt" },
    { label: "Romanian", value: "ro" },
    { label: "Russian", value: "ru" },
    { label: "Spanish", value: "es" },
    { label: "Thai", value: "th" },
    { label: "Turkish", value: "tr" },
    { label: "Vietnamese", value: "vi" },
  ];

  useEffect(() => {
    const fetchSetupData = async () => {
      if (shopOrigin) {
        try {
          const response = await axios.get("/api/check-setup-status/", {
            params: { shop: shopOrigin },
          });

          let fetchedData = formData; // Default to current form data

          if (response.data.initial_steps_completed && response.data.prefill) {
            const prefill = response.data.prefill;
            
            fetchedData = {
              shopUrl: prefill.shop_url || "",
              noContent: prefill.no_website_content || false,
              competitorUrl: prefill.competitor_url || "",
              keywords: prefill.keywords || "",
              ageRanges: prefill.age_ranges
                ? prefill.age_ranges.split(", ").filter((range) => range)
                : [],
              gender: prefill.gender || "All Genders",
              language: prefill.language || "en",
            };

            setFormData(fetchedData);
            setInitialFormData(fetchedData);
          }

          // Always check for incomplete data, regardless of API response
          const isDataIncomplete = checkIfDataIncomplete(fetchedData);
          setShowBanner(isDataIncomplete);

          // Remove the validation from here - let it be handled by user interactions
          setErrors({}); // Initialize with empty errors

        } catch (err) {
          // On error, check current form data for completeness
          const isDataIncomplete = checkIfDataIncomplete(formData);
          setShowBanner(isDataIncomplete);
          
          console.error("Error fetching setup data:", err.response?.data?.message || err.message);
          setErrors({
            general: "Failed to load saved setup data. Please try again.",
          });
        }
      }
    };

    fetchSetupData();
  }, [shopOrigin]);

  
  const checkIfDataIncomplete = (data) => {
    const hasEmptyAgeRanges = !data.ageRanges || data.ageRanges.length === 0;

    if (data.noContent) {
      // When there is no content, only age range is required
      return hasEmptyAgeRanges;
    }

    const shopUrlMissing = !data.shopUrl || data.shopUrl.trim() === '';
    return shopUrlMissing || hasEmptyAgeRanges;
  };

  const validateUrl = (url) => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      return "";
    }
    try {
      const urlObj = new URL(trimmedUrl);
      if (urlObj.protocol !== "https:") {
        return "URL must start with https:// (http:// is not allowed)";
      }
      return "";
    } catch (e) {
      return "Invalid URL format. Please enter a valid web address starting with https://";
    }
  };

  const handleInputChange = (field) => (value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    detectChanges(updatedData);

    if (field === "shopUrl" || field === "competitorUrl") {
      const error = validateUrl(value);
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else if (field === "keywords") {
      // Optional: no required error
      setErrors((prev) => ({ ...prev, keywords: "" }));
    }
  };

  
  const handleBlur = (field) => () => {
    const value = formData[field];
    let error = "";

    if (field === "shopUrl") {
      if (!formData.noContent && !value.trim()) {
        error =
          "Please provide a Shopify Shop URL or check the 'I don't have any content on my website yet' checkbox.";
      } else if (!formData.noContent) {
        error = validateUrl(value);
      }
    } else if (field === "competitorUrl") {
      if (value.trim()) {
        error = validateUrl(value);
      }
    }

    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    let newErrors = {};
    let hasError = false;

    // Required checks
    if (!formData.noContent && !formData.shopUrl.trim()) {
      newErrors.shopUrl = "Please provide a Shopify Shop URL or check the 'I don't have any content on my website yet' checkbox.";
      hasError = true;
    }
    if (formData.ageRanges.length === 0) {
      newErrors.ageRanges = "Please select at least one age range.";
      hasError = true;
    }

    // URL format validation
    if (!formData.noContent) {
      const shopUrlError = validateUrl(formData.shopUrl);
      if (shopUrlError) {
        newErrors.shopUrl = shopUrlError;
        hasError = true;
      }
    }
    if (formData.competitorUrl.trim()) {
      const competitorUrlError = validateUrl(formData.competitorUrl);
      if (competitorUrlError) {
        newErrors.competitorUrl = competitorUrlError;
        hasError = true;
      }
    }
    const competitorUrlError = validateUrl(formData.competitorUrl);
    if (competitorUrlError) {
      newErrors.competitorUrl = competitorUrlError;
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    const payload = {
      shop: shopOrigin,
      shop_url: formData.shopUrl,
      no_website_content: formData.noContent,
      competitor_url: formData.competitorUrl,
      keywords: formData.keywords,
      age_ranges: JSON.stringify(formData.ageRanges),
      gender: formData.gender,
      language: formData.language,
      step1_completed: true,
      initial_steps_completed: true,
    };

    try {
      await axios.post("/api/save-setup/", payload, {
        headers: { "Content-Type": "application/json" },
      });

      setInitialFormData(formData);
      setHasChanges(false);
      setHasInteracted(false);
      navigate(`/dashboard?shop=${encodeURIComponent(shopOrigin || "")}`);
    } catch (err) {
      setErrors({
        general:
          err.response?.data?.message ||
          "Failed to save setup data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    setFormData(initialFormData);
    setHasChanges(false);
    setHasInteracted(false);
    setErrors({});
  };

  const handleAgeRangeChange = (value) => (checked) => {
    const updated = checked
      ? [...formData.ageRanges, value]
      : formData.ageRanges.filter((age) => age !== value);

    const updatedData = { ...formData, ageRanges: updated };
    setFormData(updatedData);
    detectChanges(updatedData);

    // Validate age ranges
    const error = updated.length === 0 ? "Please select at least one age range." : "";
    setErrors((prev) => ({ ...prev, ageRanges: error }));
  };

  const handleGenderChange = (gender) => {
    const updatedData = { ...formData, gender };
    setFormData(updatedData);
    detectChanges(updatedData);
  };

  const handleNoContentChange = (value) => {
    const updatedData = { ...formData, noContent: value };
    setFormData(updatedData);
    detectChanges(updatedData);

    // Handle shopUrl error based on noContent
    if (value) {
      setErrors((prev) => ({ ...prev, shopUrl: "" }));
    } else if (!updatedData.shopUrl.trim()) {
      setErrors((prev) => ({
        ...prev,
        shopUrl: "Please provide a Shopify Shop URL or check the 'I don't have any content on my website yet' checkbox.",
      }));
    } else {
      const error = validateUrl(updatedData.shopUrl);
      setErrors((prev) => ({ ...prev, shopUrl: error }));
    }
  };

  const detectChanges = (newData) => {
    const changed = JSON.stringify(newData) !== JSON.stringify(initialFormData);
    setHasChanges(changed);
    if (!hasInteracted) setHasInteracted(true);
  };

  const customStyles = `
    .Polaris-ButtonGroup {
      margin-left: 0px;
      margin-top: 6px;
      border: 1px solid black;
      width: fit-content;
      padding: 1px;
      border-radius: 10px;
    }

    .Polaris-Button--pressed {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }

    .Polaris-Button--pressed:hover,
    .Polaris-Button--pressed:focus,
    .Polaris-Button--pressed:active {
      background-color: #000 !important;
      color: #fff !important;
      border-color: #000 !important;
    }
  `;

  return (
    <Page title="Settings">
    <style>{customStyles}</style>

      {showBanner && (
        <div style={{marginBottom: "15px", marginTop: "-15px"}}>
          <Box paddingBlockEnd="4">
            <Banner
              title="Before you proceed, please fill in the information below:"
              tone="warning"
            >
            </Banner>
          </Box>
        </div>
      )}

      {errors.general && (
        <Box paddingBlockEnd="4">
          <Text color="critical">{errors.general}</Text>
        </Box>
      )}

      <div style={{marginBottom:"15px"}}>
        <Card>
          <Box padding="4">
            <FormLayout>
              {/* --- Language Selection --- */}
              <Select
                label="Select the primary language for your content"
                options={languageOptions}
                value={formData.language}
                onChange={handleInputChange("language")}
                error={errors.language}
              />
              {/* --- Shop Section --- */}
              <TextField
                label="Shopify Shop URL"
                placeholder="e.g., https://www.example.com"
                value={formData.shopUrl}
                onChange={handleInputChange("shopUrl")}
                onBlur={handleBlur("shopUrl")}
                error={errors.shopUrl}
                autoComplete="off"
                disabled={formData.noContent}
              />

              <Checkbox
                label="I don't have any content on my website yet"
                checked={formData.noContent}
                onChange={handleNoContentChange}
                disabled={formData.shopUrl.trim().length > 0}
              />

              {/* --- Competitor Section --- */}
              <TextField
                label="Competitor website"
                placeholder="https://www.example.com"
                value={formData.competitorUrl}
                onChange={handleInputChange("competitorUrl")}
                onBlur={handleBlur("competitorUrl")}
                error={errors.competitorUrl}
                helpText="Helps AI understand tone and style"
              />
              <TextField
                label="Product keywords"
                placeholder="e.g., lipstick, vegan skincare"
                value={formData.keywords}
                onChange={handleInputChange("keywords")}
                onBlur={handleBlur("keywords")}
                error={errors.keywords}
                helpText="Comma separated"
              />

              {/* --- Audience Age --- */}
              <div>
                <Text variant="bodyMd" fontWeight="medium">
                  Audience Age Range
                </Text>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                  }}
                >
                  {["16-22", "23-32", "33-45", "46-60", "60+"].map((range) => (
                    <Checkbox
                      key={range}
                      label={range}
                      checked={formData.ageRanges.includes(range)}
                      onChange={handleAgeRangeChange(range)}
                    />
                  ))}
                  {errors.ageRanges && (
                    <Text color="critical">{errors.ageRanges}</Text>
                  )}
                </div>
              </div>

              {/* --- Audience Gender --- */}
              <div>
                <Text variant="bodyMd" fontWeight="medium">
                  Audience Gender
                </Text>
                <div style={{ display: "flex", gap: "8px" }}>
                  {["Male", "Female", "All Genders"].map((gender) => (
                    <Button
                      key={gender}
                      pressed={formData.gender === gender}
                      onClick={() => handleGenderChange(gender)}
                    >
                      {gender}
                    </Button>
                  ))}
                </div>
                {errors.gender && <Text color="critical">{errors.gender}</Text>}
              </div>
            </FormLayout>
          </Box>
        </Card>
        <div
          style={{
            marginTop: "20px",
            marginBottom: "20px",
            display: "flex",
            justifyContent: "right",
            alignItems: "center",
            marginRight:"15px"
          }}
        >
          <Button
            variant="primary"
            onClick={handleSave}
            loading={isLoading}
            disabled={isLoading}
          >
            Submit
          </Button>
        </div>
      </div>
    </Page>
  );
}

export default Settings;